/**
 * Vinz switches (plan 21 D8b): GET/POST /console/switches. Everyone signed in reads them; only an operator (the API's
 * allowlist, which answers `canEdit`) may change one. A change is conditional on the version the page last read, so a
 * concurrent change fails cleanly with 409 instead of being overwritten.
 */

export type SwitchName = 'inRunReserve' | 'backstopAct';
/** `item`: set in Vinz's config. `default`: set nowhere, so the fail-safe (off). */
export type SwitchSource = 'item' | 'default';

export interface SwitchesResponse {
  /** 0 = no config item yet. */
  version: number;
  canEdit: boolean;
  switches: Record<SwitchName, { value: boolean; source: SwitchSource }>;
  /** Changed only from the CLI, so the console shows it read-only. */
  probeJobWorkflowRefs: { value: string[]; source: SwitchSource };
  updatedAt?: string;
  updatedBy?: string;
  via?: 'console' | 'cli';
}

export interface SwitchInfo { name: SwitchName; label: string; on: string; off: string }

export const SWITCHES: SwitchInfo[] = [
  { name: 'inRunReserve', label: 'In-run reservation',
    on: 'A manual branch deploy reserves from inside its own run.', off: 'The old two-run fallback.' },
  { name: 'backstopAct', label: 'Backstop acts',
    on: 'The backstop sweep acts on what it finds.', off: 'Dry run: the backstop only logs.' },
];

/** Both fail safe: what a switch is when it is set nowhere. */
export const SWITCH_DEFAULT = false;

export const SOURCE_LABEL: Record<SwitchSource, string> = {
  item: 'Vinz config',
  default: 'Not set (default)',
};

/** The API's answers to a switch POST - each one is an outcome to show, not a failure to throw. */
export const SWITCH_STATUSES = [400, 403, 409, 504];

export interface SwitchesIo {
  post: (body: { name: SwitchName; value: boolean; expectedVersion: number }) =>
    Promise<{ status: number; body: (SwitchesResponse & { ok?: true }) | { ok: false; error?: string; message?: string } }>;
}

/**
 * `state`: the new state, straight from the POST. `recheck`: the page's copy may be out of date or the outcome is
 * unknown, so it must be read again before anything else is shown or offered.
 */
export type SwitchOutcome =
  | { ok: true; message: string; state: SwitchesResponse }
  | { ok: false; message: string; recheck: boolean };

export async function setSwitch(io: SwitchesIo, info: SwitchInfo, value: boolean, expectedVersion: number): Promise<SwitchOutcome> {
  const { status, body } = await io.post({ name: info.name, value, expectedVersion });
  const message = 'message' in body && body.message ? body.message : undefined;
  if (status === 200 && 'version' in body) {
    const state = { ...body };
    delete state.ok;
    return { ok: true, message: `${info.label} turned ${value ? 'on' : 'off'}.`, state };
  }
  if (status === 409) {
    return { ok: false, recheck: true,
      message: 'Someone changed the switches since this page loaded, so nothing was changed. Showing the current values.' };
  }
  if (status === 504) {
    return { ok: false, recheck: true,
      message: "Vinz didn't confirm the change in time. Showing what it reads now: check whether it landed before trying again." };
  }
  if (status === 403) return { ok: false, recheck: true, message: message ?? 'Only a Vinz operator can change switches.' };
  return { ok: false, recheck: false, message: message ?? `That didn't work (HTTP ${status}).` };
}
